import DefaultsManager from '../../../managers/defaults-manager';
import DomainModel from '../../../domain-model';
import DragTool from '../../../tools/drag-tool';
import Signal from '../../../control-flow/signal';
import Tool from '../../../tools/tool';
import logging from '../../../logging';
import { OrthographicCamera, Scene, Vector2 } from 'three';
import { Picker } from '../../../view3d/picker';
import { bumpJobPriority, renderObjectURL } from '../../../view3d/thumbnail-renderer';
import { s2wElt } from '../../../view3d';

const log = logging('tools');

export type AddToolContext = {
    camera: OrthographicCamera;
    canvas: HTMLCanvasElement;
    defaults: DefaultsManager;
    picker: Picker;
    scene: Scene;
};

export abstract class AddTool extends Tool {
    ctx: AddToolContext;
    dt: DragTool;

    worldPosition: Vector2 = new Vector2();

    onSuccess = new Signal<[DomainModel]>('onSuccess', 'tools');
    onDisplayChange = new Signal('onDisplayChange', 'tools');

    icon: HTMLElement;
    iconJobId: number = null;

    constructor(icon: HTMLElement, ctx: AddToolContext) {
        super();
        log.debug('AddTool::constructor');

        this.icon = icon;
        this.icon.addEventListener('bin-opened', () => {
            if(this.iconJobId != null) {
                bumpJobPriority(this.iconJobId);
            }
        });

        this.ctx = ctx;

        this.dt = new DragTool(icon, this.ctx.canvas, this);

        this.dragenter = this.dragenter.bind(this);
        this.dragover = this.dragover.bind(this);
        this.dragleave = this.dragleave.bind(this);
    }

    /**
     * We are enabled when in front view.
     */
    enable() {
        log.debug('AddTool::enable');
        this.dt.enable();
    }

    /**
     * We are disabled when in perspective view.
     */
    disable() {
        log.debug('AddTool::disable');
        this.dt.disable();
    }

    /**
     * Our begin method is called by the drag tool when the drag begins, but we
     * don't really have anything to do.
     */
    begin() {
        log.debug('AddTool::begin (no-op)');
    }

    abstract dragenter(): void;
    abstract dragover(event: PointerEvent, target: HTMLElement): void;
    abstract dragleave(): void;

    /**
     * Set our worldPosition variable. The implementations of dragover probably
     * want to do this first thing.
     */
    protected updateWorldPosition(event: PointerEvent, target: HTMLElement) {
        s2wElt(this.ctx.camera, event, target, this.worldPosition);
    }

    /**
     * This is typically called in the constructor of the AddTool subclass.
     */
    updateThumbnail = async (model: DomainModel) => {
        try {
            const [jobId, promise] = renderObjectURL(model);
            this.iconJobId = jobId;

            const
                url = await promise,
                img = (this.icon instanceof Image) ? this.icon : this.icon.querySelector('img');
            img.src = url;
        } catch(error) {
            log.error("Unable to update thumbnail", error);
        }
    }

}
export default AddTool;
